import { useState } from "react"
import styled from "styled-components"

import { useLazyGetContractorsQuery } from "store/api/contractorsApi"
import { useAppSelector } from "store/hooks"
import { ApiTypes, UITypes } from "types"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as TopArrow } from "assets/svg/contractor-top-arrow.svg"

import { convertContractorsToTableRows } from "utils/convertData"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AddContractorModal } from "modals/AddContractorModal"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"

const ReverseArrow = styled(TopArrow)`
  transform: rotate(180deg);
`

const Bottom16Button = styled(ButtonNew)`
  margin-right: 15px;
`

const StyledTabsWrapper = styled.div`
  width: 100%;
`

const TabsContainer = styled.div`
  display: flex;
  margin-top: 16px;
  width: 100%;
  border-bottom: 1px solid var(--border-strong);
  margin-bottom: 15px;
`

const Tab = styled.button<{ $active: boolean; $tabColor?: string }>`
  color: ${({ $active, $tabColor }) => {
    if ($tabColor) {
      return $tabColor
    }
    return $active ? "#334155" : "#64748b"
  }};
  margin-right: 24px;
  font-size: 14px;
  font-weight: 500;
  padding-bottom: 14px;
  margin-bottom: -1px;
  border: none;
  border-bottom: 1px solid
    ${({ $active, $tabColor }) => {
      if ($active && $tabColor) {
        return $tabColor
      }
      return $active ? "#2db2c8" : "transparent"
    }};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  opacity: ${({ $active }) => ($active ? 1 : 0.7)};

  &:hover {
    opacity: 1;
    color: ${({ $tabColor }) => $tabColor || "#334155"};
  }
`

interface HeaderProps {
  tableHeaderCells: UITypes.TableHeaderCell[]
  meta?: ApiTypes.Model.Pagination
  onAddContractorSuccess: () => void
}

const Header = ({ tableHeaderCells, meta, onAddContractorSuccess }: HeaderProps) => {
  const [showAddContractorModal, setShowAddContractorModal] = useState(false)
  const { setUrlQueryParams } = useUrlQueryParams()
  const { urlQueryParams } = useUrlQueryParams()

  const statusOptions = useAppSelector(state => state.tempData.contractorAccountStatusOptions) || []

  const statusTabs = [
    { label: "All Contractors", value: "", color: undefined },
    ...statusOptions.map(option => ({
      label: option.label,
      value: option.value as string,
      color: option.color,
    })),
  ]

  const activeStatusTab = urlQueryParams.account_status_id || ""

  const getAdditionalParams = (): Record<string, string> => {
    if (urlQueryParams.top15 || urlQueryParams.bottom15) {
      return {
        order_column: "installed",
        order_type: urlQueryParams.top15 ? "DESC" : "ASC",
        is_enabled: "1",
      }
    }

    return { is_enabled: "1" }
  }

  const getCSVExportMeta = () => {
    if (meta && (urlQueryParams.top15 || urlQueryParams.bottom15)) {
      return {
        ...meta,
        total: 15,
      }
    }

    return meta
  }

  const { handleExport } = useExportCSV({
    params: { ...urlQueryParams, ...getAdditionalParams() },
    headerCells: tableHeaderCells,
    meta: getCSVExportMeta(),
    fileName: "contractors",
    convertData: convertContractorsToTableRows,
    useLazyQuery: useLazyGetContractorsQuery,
  })

  const onAddSingleClick = () => {
    setShowAddContractorModal(true)
  }

  const setSearchParam = (param: "top15" | "bottom15") => {
    setUrlQueryParams({ [param]: true }, true)
  }

  const handleTop15Click = () => {
    setSearchParam("top15")
  }

  const handleBottom15Click = () => {
    setSearchParam("bottom15")
  }

  const handleAddContractorSuccess = () => {
    setShowAddContractorModal(false)
    onAddContractorSuccess()
  }

  const handleStatusTabClick = (status: string) => {
    if (status === "") {
      setUrlQueryParams({ account_status_id: null }, true)
    } else {
      setUrlQueryParams({ account_status_id: status }, true)
    }
  }

  return (
    <>
      <nav className={styles.tableNavWrapper}>
        <ButtonNew onClick={onAddSingleClick} color="blue">
          Add contractor
        </ButtonNew>
        <div className={styles.tableNavButtonsWrapper}>
          <ButtonNew
            color="tropicalCyan"
            onClick={handleTop15Click}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            <TopArrow />
            Top 15
          </ButtonNew>
          <Bottom16Button
            color="tropicalCyan"
            onClick={handleBottom15Click}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            <ReverseArrow />
            Bottom 15
          </Bottom16Button>
          <EditColumnsButton tableName="admin_contractors_list" tableHeaderCells={tableHeaderCells} />
          <ButtonNew
            color="tropicalCyan"
            onClick={handleExport}
            className={styles.tableNavItem}
            disabled={meta?.total === 0}
          >
            Export
          </ButtonNew>
        </div>
      </nav>
      <StyledTabsWrapper>
        <TabsContainer>
          {statusTabs.map(tab => (
            <Tab
              key={tab.value}
              $active={activeStatusTab === tab.value}
              $tabColor={tab.color}
              onClick={() => handleStatusTabClick(tab.value)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>
      </StyledTabsWrapper>
      {showAddContractorModal && (
        <AddContractorModal onCancel={() => setShowAddContractorModal(false)} onSuccess={handleAddContractorSuccess} />
      )}
    </>
  )
}

export default Header
